/* The schedule. One page, two routes: /schedule and /schedule/2026-08-24.
 *
 * THE DAY IS IN THE URL, and that is the whole reason this page is worth
 * building rather than dumping a week into a table. "Look at Saturday" is a
 * link somebody can paste into a group chat, and a link that survives being
 * pasted into another time zone — the date in the path is the STATION's
 * calendar date, so it means the same Saturday in Lisbon and in Chicago.
 *
 * THE DAY IS A LIST, NOT A GRID. Every station site we studied that draws a
 * seven-column time grid is unreadable on a phone, and roughly everybody
 * reading a schedule is on a phone. NTS uses a list. We use a list. The grid
 * survives, but only as the week strip: seven columns of real programming
 * density, kept as a NAVIGATIONAL graphic — you press a column to move to that
 * day, you do not read the programme off it.
 *
 * THE DARK HOURS ARE DRAWN. The API deliberately does not return gaps as slots
 * (docs/API.md), so the client decides how to say "nobody is in the building".
 * We say it out loud, as a fallback block with the hours on it. This station is
 * dark most of the day; a schedule that quietly omits eighteen hours of it is a
 * schedule that lies, and the first time a listener tunes in at 14:00 to hear
 * an archive shuffle they will know it lied.
 *
 * TIME ZONE IS A CONTROL, NOT AN ASSUMPTION. A row that says 20:00 and does not
 * say where 20:00 is has told a listener in Lisbon nothing and told one in
 * Chicago something false. The choice is written to the one shared localStorage
 * key that every other surface reads.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import {
  Empty,
  ErrorNote,
  ScheduleRow,
  Section,
  Spinner,
  TimezoneToggle,
  hostNames,
} from '../components';
import {
  STATION_TZ,
  formatDayDate,
  formatFullDate,
  formatTime,
  isoDate,
  readTzMode,
  shiftIsoDate,
  stationToday,
  tzLabel,
} from '../format';
import type { TzMode } from '../format';
import { usePlayer } from '../player';
import type { ScheduleDay, ScheduleResponse, SlotCard } from '../types';

/* ----------------------------------------------------------------- shape */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'failed'; error: unknown };

const LOADING = { status: 'loading' } as const;

/** The switcher, the strip and the request all agree on seven. */
const WINDOW_DAYS = 7;

/**
 * A gap shorter than this is a changeover, not an unattended stretch. Drawing
 * fifteen minutes of archive shuffle as its own row would make a normal evening
 * read as far more broken up than it is.
 */
const MIN_DARK_MS = 15 * 60 * 1000;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/* ------------------------------------------------------- station calendar
 *
 * format.ts renders moments and never does arithmetic on them, which is the
 * right split — but the dark hours need the one thing it does not expose: the
 * UTC instant at which a station-local day begins, and how long that day is.
 * The last Sunday in March is 23 hours long and the last Sunday in October is
 * 25, so the answer is asked of Intl with an explicit zone rather than computed
 * by adding 86,400 seconds to anything.
 */

function zoneOffsetMs(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));

  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? Number.NaN);

  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );
  return Number.isFinite(asIfUtc) ? asIfUtc - at : 0;
}

/** The UTC instant of midnight, station time, on a `YYYY-MM-DD`. */
function stationMidnight(date: string): number {
  const guess = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(guess)) return Number.NaN;
  // Two passes: the first lands within an hour, the second is exact even when
  // the guess and the answer sit on opposite sides of a clock change.
  const once = guess - zoneOffsetMs(guess, STATION_TZ);
  return guess - zoneOffsetMs(once, STATION_TZ);
}

/** A real `YYYY-MM-DD` that round-trips. `2026-02-31` does not. */
function isRealDate(value: string | undefined): value is string {
  if (!value || !DATE_PATTERN.test(value)) return false;
  return isoDate(value, 'station') === value;
}

/** Whole days from `a` to `b`, both bare dates. Negative when b is earlier. */
function dayOffset(a: string, b: string): number {
  const from = Date.parse(`${a}T00:00:00Z`);
  const to = Date.parse(`${b}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.round((to - from) / 86_400_000);
}

/* -------------------------------------------------------- the dark hours */

interface Segment {
  /** The booking, or null for an unattended stretch. */
  slot: SlotCard | null;
  startsAt: string;
  endsAt: string;
  /** Minutes INSIDE this station day, which is what the density strip draws. */
  minutes: number;
}

/**
 * The archive shuffle, as a row.
 *
 * A synthesised SlotCard rather than a bespoke component: ScheduleRow already
 * knows what `kind: 'fallback'` looks like — the surface field, the muted
 * title, the word "Fallback" in the state column, the 24/7 sleeve — and a
 * second row component drawing the same thing slightly differently is how a
 * schedule stops reading as one printed sheet.
 *
 * The catalogue number is deliberately empty. There is no booking here, and
 * inventing one would put a number on screen that resolves to nothing.
 */
function darkSlot(startsAt: string, endsAt: string): SlotCard {
  return {
    catalogue_no: '',
    title: 'Archive shuffle',
    starts_at: startsAt,
    ends_at: endsAt,
    kind: 'fallback',
    status: 'scheduled',
    is_repeat: false,
    hosts: [],
    genres: [],
    artwork_color: 'deep',
    episode_catalogue_no: null,
  };
}

/**
 * One station day, in order, with nothing hidden.
 *
 * A slot belongs to the day it STARTS on — a show booked 23:00–01:00 is
 * Saturday's show and not half of each, which is how the station, the DJ and
 * the poster all describe it. Its minutes are clamped to the day so the density
 * strip stays a picture of one day, but the ROW keeps the real 23:00 — 01:00.
 */
function daySegments(date: string, slots: readonly SlotCard[]): Segment[] {
  const dayStart = stationMidnight(date);
  const dayEnd = stationMidnight(shiftIsoDate(date, 1));

  const ordered = [...slots].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));

  if (!Number.isFinite(dayStart) || !Number.isFinite(dayEnd)) {
    return ordered.map((slot) => ({
      slot,
      startsAt: slot.starts_at,
      endsAt: slot.ends_at,
      minutes: 60,
    }));
  }

  const minutes = (from: number, to: number): number => Math.max(1, Math.round((to - from) / 60000));

  const out: Segment[] = [];
  let cursor = dayStart;

  for (const slot of ordered) {
    const start = Date.parse(slot.starts_at);
    const end = Date.parse(slot.ends_at);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

    if (start - cursor >= MIN_DARK_MS) {
      out.push({
        slot: null,
        startsAt: new Date(cursor).toISOString(),
        endsAt: new Date(start).toISOString(),
        minutes: minutes(cursor, start),
      });
    }

    const shownFrom = Math.max(start, cursor, dayStart);
    const shownTo = Math.min(Math.max(end, shownFrom), dayEnd);
    out.push({
      slot,
      startsAt: slot.starts_at,
      endsAt: slot.ends_at,
      minutes: minutes(shownFrom, shownTo),
    });
    cursor = Math.max(cursor, end);
  }

  if (dayEnd - cursor >= MIN_DARK_MS) {
    out.push({
      slot: null,
      startsAt: new Date(cursor).toISOString(),
      endsAt: new Date(dayEnd).toISOString(),
      minutes: minutes(cursor, dayEnd),
    });
  }

  return out;
}

/** How long a station day is, in seconds. 23 or 25 hours twice a year. */
function daySeconds(date: string): number {
  const start = stationMidnight(date);
  const end = stationMidnight(shiftIsoDate(date, 1));
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 86_400;
  return Math.max(0, Math.round((end - start) / 1000));
}

/* ------------------------------------------------------------------ words */

/** `CEST`, `UTC`, or nothing when the zone has no short name worth printing. */
function zoneWord(tz: TzMode, at?: string): string {
  const label = tzLabel(tz, at);
  if (label === 'UTC') return 'UTC';
  const parts = label.split('·');
  return parts.length > 1 ? parts[parts.length - 1].trim() : '';
}

/** `Mon 24 August 2026`, composed so the weekday survives en-GB's comma. */
function dayHeading(date: string, tz: TzMode): string {
  const weekday = formatDayDate(date, tz).split(' ')[0] ?? '';
  return `${weekday} ${formatFullDate(date, tz)}`.trim();
}

/** `24 Aug` — the big line on a day pill. */
function pillDate(date: string, tz: TzMode): string {
  const words = formatDayDate(date, tz).split(' ');
  return words.length >= 3 ? words.slice(1).join(' ') : formatDayDate(date, tz);
}

function weekdayOf(date: string, tz: TzMode): string {
  return formatDayDate(date, tz).split(' ')[0] ?? '';
}

/** `19` off `19:00`, for the two-word dark label in the density strip. */
function hourOf(iso: string, tz: TzMode): string {
  return formatTime(iso, tz).split(':')[0] ?? '';
}

/**
 * `3 shows · 6 hours booked · 18 hours archive shuffle`.
 *
 * The two halves always add up to the day, which is the point: the shuffle is
 * not filler we are embarrassed about, it is most of what the stream carries
 * and the header says so before anyone has to scroll to find out.
 */
function dayMeta(day: ScheduleDay): string {
  const booked = day.slots.filter((slot) => slot.status !== 'cancelled').length;
  const total = daySeconds(day.date);
  const liveHours = Math.round(day.live_seconds / 3600);
  const darkHours = Math.max(0, Math.round((total - day.live_seconds) / 3600));

  const shows = booked === 0 ? 'Nothing booked' : `${booked} ${booked === 1 ? 'show' : 'shows'}`;
  const parts = [shows];
  if (liveHours > 0) parts.push(`${liveHours} ${liveHours === 1 ? 'hour' : 'hours'} booked`);
  if (darkHours > 0) {
    parts.push(`${darkHours} ${darkHours === 1 ? 'hour' : 'hours'} archive shuffle`);
  }
  return parts.join(' · ');
}

function genreHref(slug: string): string {
  return `/genre/${encodeURIComponent(slug)}`;
}

function dayHref(date: string): string {
  return `/schedule/${date}`;
}

/* ---------------------------------------------------------- week strip */

/**
 * Seven columns of real programming density, and it is a CONTROL rather than a
 * reading surface: every column is a link to that day, every block is sized by
 * how long the booking actually runs, and the dark stretches are drawn at their
 * real proportion rather than as a uniform gap. That is the honest picture of a
 * small station — a few tall blocks in the evening and a lot of dark.
 *
 * It is aria-hidden below the day pills would be wrong: the pills say "24 Aug"
 * and this says what is ON the 24th, so it is announced, as a list of links.
 */
function WeekStrip({
  days,
  selected,
  today,
  tz,
}: {
  days: readonly ScheduleDay[];
  selected: string;
  today: string;
  tz: TzMode;
}) {
  return (
    <nav className="week" aria-label="The week at a glance">
      {days.map((day) => {
        const segments = daySegments(day.date, day.slots);
        const isSelected = day.date === selected;
        const booked = day.slots.filter((slot) => slot.status !== 'cancelled').length;

        return (
          <Link
            key={day.date}
            className={isSelected ? 'wcol wcol--sel' : 'wcol'}
            to={dayHref(day.date)}
            aria-current={isSelected ? 'date' : undefined}
          >
            <span className="mono wcol__d">
              {weekdayOf(day.date, tz)} {pillDate(day.date, tz).split(' ')[0]}
              {day.date === today ? <span className="wcol__today"> · Today</span> : null}
            </span>

            <span className="vh">
              {booked === 0
                ? 'No shows booked.'
                : `${booked} ${booked === 1 ? 'show' : 'shows'} booked.`}
            </span>

            {booked === 0 ? (
              <span className="wgap wgap--allday" aria-hidden="true">
                No shows booked
              </span>
            ) : (
              segments.map((segment) =>
                segment.slot ? (
                  <span
                    key={`b-${segment.startsAt}-${segment.slot.catalogue_no}`}
                    className={`wblk wblk--${segment.slot.artwork_color}`}
                    style={{ flexGrow: segment.minutes }}
                    aria-hidden="true"
                  >
                    {formatTime(segment.startsAt, tz)} {segment.slot.title}
                    {segment.slot.is_repeat ? ' (R)' : ''}
                  </span>
                ) : (
                  <span
                    key={`g-${segment.startsAt}`}
                    className="wgap"
                    style={{ flexGrow: segment.minutes }}
                    aria-hidden="true"
                  >
                    Dark {hourOf(segment.startsAt, tz)}–{hourOf(segment.endsAt, tz)}
                  </span>
                ),
              )
            )}
          </Link>
        );
      })}
    </nav>
  );
}

/* -------------------------------------------------------------------- page */

/* Temporary public gate: keep every schedule URL valid without exposing the
   programme until the next run is ready to announce. */
const PUBLIC_SCHEDULE_VISIBLE = false;

function ScheduleSoon() {
  useEffect(() => {
    document.title = 'Schedule coming soon · Higher Ground Radio';
  }, []);

  return (
    <div className="schedule">
      <Section level={1} title="Schedule" meta="Programming update" />
      <Empty
        kind="Coming soon"
        title="The full schedule will be up soon."
        actions={
          <>
            <Link className="btn" to="/">
              Back to the station
            </Link>
            <Link className="btn-ghost" to="/archive">
              Browse the archive
            </Link>
          </>
        }
      >
        We are putting the next run of shows in order. The station keeps playing in the meantime,
        and we will publish the full programme here shortly.
      </Empty>
    </div>
  );
}

export default function Schedule() {
  return PUBLIC_SCHEDULE_VISIBLE ? <PublishedSchedule /> : <ScheduleSoon />;
}

function PublishedSchedule() {
  const { date: dateParam } = useParams();
  const { live } = usePlayer();

  const today = stationToday();

  /* A path that is not a real date is not a schedule for anything. Rather than
     guess at what was meant, we go to today — the route still exists, so a
     404 would be a lie about the address as well as about the date. */
  const invalid = dateParam !== undefined && !isRealDate(dateParam);
  const selected = isRealDate(dateParam) ? dateParam : today;

  const [tz, setTz] = useState<TzMode>(() => readTzMode());
  const [reloads, setReloads] = useState(0);
  const [week, setWeek] = useState<Load<ScheduleResponse>>(LOADING);

  /* The seven days on screen. The anchor is held rather than derived from the
     selection, so pressing Wednesday does not shove the week sideways under the
     pointer; it only moves when the selection lands outside it, which is what
     the arrows and a deep link do. */
  const [anchorState, setAnchorState] = useState<string>(() => {
    const offset = dayOffset(today, selected);
    return offset >= 0 && offset < WINDOW_DAYS ? today : selected;
  });

  const offsetInWindow = dayOffset(anchorState, selected);
  const anchor =
    offsetInWindow >= 0 && offsetInWindow < WINDOW_DAYS ? anchorState : selected;

  useEffect(() => {
    if (anchor !== anchorState) setAnchorState(anchor);
  }, [anchor, anchorState]);

  /* One shared preference key, so the archive and the front page agree with
     this page about what 20:00 means. Another tab changing it changes this. */
  useEffect(() => {
    const onStorage = () => setTz(readTzMode());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    document.title = `Schedule · ${dayHeading(selected, tz)} · Higher Ground Radio`;
  }, [selected, tz]);

  useEffect(() => {
    if (invalid) return;
    const controller = new AbortController();
    setWeek(LOADING);

    api
      .getSchedule({ from: anchor, days: WINDOW_DAYS }, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setWeek({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setWeek({ status: 'failed', error });
      });

    return () => controller.abort();
  }, [anchor, reloads, invalid]);

  const windowDates = useMemo(
    () => Array.from({ length: WINDOW_DAYS }, (_, index) => shiftIsoDate(anchor, index)),
    [anchor],
  );

  const day = useMemo<ScheduleDay | null>(() => {
    if (week.status !== 'ready') return null;
    return week.data.days.find((candidate) => candidate.date === selected) ?? null;
  }, [week, selected]);

  const segments = useMemo(
    () => (day ? daySegments(day.date, day.slots) : []),
    [day],
  );

  if (invalid) return <Navigate to="/schedule" replace />;

  const zone = zoneWord(tz);
  const onAirCatalogueNo = live?.live ? (live.now?.catalogue_no ?? null) : null;
  const upNextCatalogueNo = live?.next[0]?.catalogue_no ?? null;
  const isToday = selected === today;

  const previousDay = shiftIsoDate(selected, -1);
  const followingDay = shiftIsoDate(selected, 1);

  return (
    <div className="schedule">
      <style href="hgr-schedule" precedence="page">
        {SCHEDULE_CSS}
      </style>

      {/* ------------------------------------------------- the day switcher */}

      <nav className="dayswitch" aria-label="Choose a day">
        <Link
          className="dayswitch__arrow"
          to={dayHref(shiftIsoDate(anchor, -WINDOW_DAYS))}
          aria-label="The seven days before these"
        >
          <span aria-hidden="true">&larr;</span>
        </Link>

        <div className="dayswitch__days">
          {windowDates.map((date) => {
            const current = date === selected;
            return (
              <Link
                key={date}
                className={current ? 'daypill daypill--sel' : 'daypill'}
                to={dayHref(date)}
                aria-current={current ? 'date' : undefined}
              >
                <span className="mono daypill__lbl">
                  {date === today ? `Today · ${weekdayOf(date, tz)}` : weekdayOf(date, tz)}
                </span>
                <span className="daypill__num">{pillDate(date, tz)}</span>
              </Link>
            );
          })}
        </div>

        <Link
          className="dayswitch__arrow dayswitch__arrow--next"
          to={dayHref(shiftIsoDate(anchor, WINDOW_DAYS))}
          aria-label="The seven days after these"
        >
          <span aria-hidden="true">&rarr;</span>
        </Link>

        {/* Always drawn, so the bar does not change width when the selection
            moves. On today it is a marked label rather than a disabled button —
            a control that cannot be focused is a control a keyboard cannot find
            out about. */}
        {isToday ? (
          <span className="mono dayswitch__today dayswitch__today--here" aria-current="date">
            Today
          </span>
        ) : (
          <Link className="mono dayswitch__today" to="/schedule">
            Back to today
          </Link>
        )}
      </nav>

      {/* ------------------------------------------------------- time zone */}

      <TimezoneToggle value={tz} onChange={setTz} />

      {/* ------------------------------------------------------ the week */}

      {week.status === 'ready' ? (
        <WeekStrip days={week.data.days} selected={selected} today={today} tz={tz} />
      ) : week.status === 'loading' ? (
        <div className="strip">
          <Spinner label="Loading the week" />
        </div>
      ) : null}

      {/* --------------------------------------------------------- the day */}

      <Section
        level={1}
        title={dayHeading(selected, tz)}
        meta={
          <>
            {day ? dayMeta(day) : 'Loading'}
            {zone ? (
              <>
                <br />
                All times {tzLabel(tz)}
              </>
            ) : null}
          </>
        }
      />

      {week.status === 'loading' ? (
        <div className="strip">
          <Spinner label="Loading the day" />
        </div>
      ) : week.status === 'failed' ? (
        <ErrorNote
          error={week.error}
          what="the schedule"
          onRetry={() => setReloads((n) => n + 1)}
        />
      ) : !day || day.slots.length === 0 ? (
        <Empty
          kind="Nothing booked"
          title={`No shows are booked on ${dayHeading(selected, tz)}.`}
          actions={
            <>
              <Link className="btn-ghost" to={dayHref(previousDay)}>
                &larr; {formatDayDate(previousDay, tz)}
              </Link>
              <Link className="btn-ghost" to={dayHref(followingDay)}>
                {formatDayDate(followingDay, tz)} &rarr;
              </Link>
              <Link className="btn" to="/submit">
                Send us a set
              </Link>
            </>
          }
        >
          The stream is still up all day — it runs Higher Ground 24/7, every set in the archive
          shuffled, whenever nobody is live. We programme one season at a time, so an empty day
          this far out usually means it has not been booked yet rather than that nothing is
          happening.
        </Empty>
      ) : (
        <ul className="rows">
          {segments.map((segment) =>
            segment.slot ? (
              <ScheduleRow
                key={`slot-${segment.slot.catalogue_no}-${segment.startsAt}`}
                slot={segment.slot}
                tz={tz}
                now={live?.server_time ?? null}
                /* The server owns "on air": it knows whether a DJ actually
                   authenticated, which comparing two timestamps cannot. */
                state={
                  onAirCatalogueNo && segment.slot.catalogue_no === onAirCatalogueNo
                    ? 'live'
                    : undefined
                }
                isNext={
                  upNextCatalogueNo !== null && segment.slot.catalogue_no === upNextCatalogueNo
                }
                showIcs
                genreHref={genreHref}
                as="li"
              />
            ) : (
              <ScheduleRow
                key={`dark-${segment.startsAt}`}
                slot={darkSlot(segment.startsAt, segment.endsAt)}
                tz={tz}
                now={live?.server_time ?? null}
                as="li"
              />
            ),
          )}
        </ul>
      )}

      {/* ------------------------------------------------------ day to day */}

      <div className="daynav">
        <Link className="btn-ghost" to={dayHref(previousDay)}>
          &larr; {formatDayDate(previousDay, tz)}
        </Link>
        <Link className="btn-ghost" to={dayHref(followingDay)}>
          {formatDayDate(followingDay, tz)} &rarr;
        </Link>
        <p className="mono dim daynav__note">
          Repeats are marked (R). Every booked slot has a calendar file. Slots go out one season
          ahead — <Link to="/submit">apply for the next one</Link>.
        </p>
      </div>

      {/* One line saying what the shuffle is, once, at the bottom — the rows
          above say "Fallback" and this is where that word is explained. */}
      <p className="mono dim schedule__note">
        Dark hours are not off-air: the stream carries Higher Ground 24/7, every set in the archive
        shuffled, until the next booking opens.
        {live?.fallback?.playing ? (
          <>
            {' '}
            On it right now: {live.fallback.playing.title}
            {hostNames(live.fallback.playing.hosts)
              ? ` w/ ${hostNames(live.fallback.playing.hosts)}`
              : ''}
            .
          </>
        ) : null}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------------- css
 *
 * The page-level furniture: the sticky day switcher, the density strip and the
 * day-to-day bar. The rows, the chips, the sleeves, the time zone control, the
 * empty state and the error note are all the kit's, used from the kit.
 *
 * Radius 0 throughout. Borders and shadows come only from the two scales in
 * tokens.css, zero blur, one direction, and every state carries a word or a
 * rule as well as a colour.
 */
const SCHEDULE_CSS = `
.schedule { display: flex; flex-direction: column; }

/* ---- the sticky seven-day switcher ---- */
.schedule .dayswitch {
  position: sticky; top: var(--nav-h); z-index: 20;
  display: flex; align-items: stretch;
  background: var(--paper);
  border-bottom: var(--bw) solid var(--edge);
}
.schedule .dayswitch__arrow {
  width: 56px; flex-shrink: 0;
  border-right: var(--bw) solid var(--edge);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem; text-decoration: none; color: var(--text);
  min-height: 44px;
}
.schedule .dayswitch__arrow--next { border-right: 0; border-left: var(--bw) solid var(--edge); }
.schedule .dayswitch__arrow:hover { background: var(--ink); color: var(--accent); }

.schedule .dayswitch__days {
  display: flex; flex: 1 1 auto; min-width: 0;
  overflow-x: auto; scrollbar-width: thin;
}
.schedule .daypill {
  flex: 1 0 auto; min-width: 96px;
  border-right: var(--bw-hair) solid var(--edge);
  padding: 13px 10px; min-height: 64px;
  display: flex; flex-direction: column; gap: 4px;
  align-items: center; justify-content: center;
  text-decoration: none; color: var(--text); white-space: nowrap;
}
.schedule .daypill__lbl { color: var(--muted); }
.schedule .daypill__num {
  font-size: 1.1875rem; line-height: 1;
  font-variation-settings: 'wdth' 96, 'wght' 800; text-transform: uppercase;
}
.schedule .daypill:hover { background: var(--ink); color: var(--accent); }
.schedule .daypill:hover .daypill__lbl { color: var(--accent); }
/* Selected is an accent field AND an inset rule. The rule is the half that
   survives greyscale, which is the only reason it is there — and it is drawn in
   --on-accent rather than --edge because --edge IS the accent's companion blue
   in dark mode, and a blue
   rule inset into an accent field is no rule at all. */
.schedule .daypill--sel {
  background: var(--accent); color: var(--on-accent);
  box-shadow: inset 0 0 0 var(--bw) var(--on-accent);
}
.schedule .daypill--sel .daypill__lbl { color: var(--on-accent); }
.schedule .daypill--sel:hover { box-shadow: inset 0 0 0 var(--bw) var(--accent); }
.schedule .daypill--sel:hover .daypill__lbl { color: var(--accent); }

.schedule .dayswitch__today {
  flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  min-width: 116px; min-height: 44px; padding: 0 14px;
  border-left: var(--bw) solid var(--edge);
  text-decoration: none; color: var(--text); text-align: center;
}
.schedule .dayswitch__today:hover { background: var(--ink); color: var(--accent); }
.schedule .dayswitch__today--here {
  color: var(--muted);
  box-shadow: inset 0 0 0 var(--bw-chip) var(--edge);
}

/* ---- the week, as a density graphic you can press ---- */
.schedule .week {
  display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 0;
  border-bottom: var(--bw) solid var(--edge);
}
.schedule .wcol {
  border-right: var(--bw-hair) solid var(--edge);
  padding: 12px 10px 14px; min-height: 196px; min-width: 0;
  display: flex; flex-direction: column; gap: 5px;
  text-decoration: none; color: var(--text);
}
.schedule .wcol:last-child { border-right: 0; }
.schedule .wcol--sel { background: var(--surface); box-shadow: inset 0 0 0 var(--bw-on) var(--edge); }
.schedule .wcol:hover { background: var(--surface); }
.schedule .wcol__d { margin-bottom: 4px; }
.schedule .wcol__today { color: var(--muted); }

.schedule .wblk {
  border: var(--bw-chip) solid var(--edge);
  padding: 5px 6px; min-height: 26px; overflow: hidden;
  font-family: var(--ff-mono); font-weight: 500; font-size: .625rem;
  letter-spacing: .04em; text-transform: uppercase; line-height: 1.3;
  background: var(--paper); color: var(--text);
}
.schedule .wblk--sky     { background: var(--sky);       color: var(--on-sky); }
.schedule .wblk--cyan    { background: var(--cyan);    color: var(--on-cyan); }
.schedule .wblk--magenta { background: var(--magenta); color: var(--on-magenta); }
.schedule .wblk--blue    { background: var(--blue);    color: var(--on-blue); }
.schedule .wblk--deep    { background: var(--deep);    color: var(--on-deep); }
.schedule .wblk--ink     { background: var(--ink);     color: var(--accent); }
.schedule .wblk--paper   { background: var(--paper);   color: var(--text); }

/* Dark hours are DRAWN, at their real proportion. Dashed, so they never read
   as a booking somebody forgot to label. */
.schedule .wgap {
  border: var(--bw-hair) dashed var(--muted);
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  font-family: var(--ff-mono); font-weight: 500; font-size: .5625rem;
  letter-spacing: .08em; text-transform: uppercase; color: var(--muted);
  min-height: 22px; text-align: center;
}
.schedule .wgap--allday { flex-grow: 1; }

/* ---- the day, as a list ---- */
.schedule .rows { list-style: none; margin: 0; padding: 0; }
.schedule .rows > :last-child { border-bottom: var(--bw) solid var(--edge); }

/* ---- day to day ---- */
.schedule .daynav {
  padding: 28px clamp(12px, 4vw, 28px);
  display: flex; gap: 20px; align-items: center; flex-wrap: wrap;
  border-bottom: var(--bw) solid var(--edge);
}
.schedule .daynav__note { margin: 0 0 0 auto; line-height: 1.7; max-width: 52ch; }
.schedule .schedule__note {
  margin: 0; padding: 18px clamp(12px, 4vw, 28px) 26px;
  line-height: 1.7; max-width: 84ch;
}

.schedule .strip { padding: 24px clamp(12px, 4vw, 28px); border-bottom: var(--bw) solid var(--edge); }

@media (max-width: 900px) {
  /* Seven columns of eleven characters is not a graphic, it is a smear. The
     strip scrolls sideways instead, and the day list below is the reading
     surface it always was. */
  .schedule .week {
    display: flex; overflow-x: auto; scrollbar-width: thin;
  }
  .schedule .wcol { flex: 0 0 44vw; min-height: 168px; }
  .schedule .daypill { min-width: 84px; padding: 10px 8px; }
  .schedule .dayswitch__arrow { width: 44px; }
  .schedule .dayswitch__today { min-width: 88px; padding: 0 8px; }
  .schedule .daynav__note { margin-left: 0; }
}
@media (max-width: 620px) {
  .schedule .wcol { flex: 0 0 68vw; }
  .schedule .daynav { padding: 18px 12px; gap: 12px; }
}
`;
